/**
 * @description 登录、获取用户信息、退出登录、清除token逻辑，不建议修改
 */
import { useAclStore } from './acl';
import { useTabsStore } from './tabs';
import { useRoutesStore } from './routes';
import { useSettingsStore } from './settings';
import { login, logout, socialLogin } from '@/api/user';
import { getToken, removeToken, setToken } from '@/utils/token';
import { resetRouter } from '@/router';
import { tokenName } from '@/config';
import { ensureToken } from '@/utils/ccflowToken';
import { gp } from '@gp';
import { closeAllModals } from '~/library/plugins/modal.helper';
import { getBaseSetting } from '~/src/api/shared';
export const useUserStore = defineStore('user', {
  state: () => ({
    token: getToken(),
    userId: '',
    username: '游客',
    avatar: 'https://i.gtimg.cn/club/item/face/img/2/15922_100.gif',
    linkSurname: ''
  }),
  getters: {
    getToken: state => state.token,
    getUserId: state => state.userId,
    getUsername: state => state.username,
    getAvatar: state => state.avatar
  },
  actions: {
    /**
     * @description 设置token
     * @param {*} token
     */
    setToken(token) {
      this.token = token;
      setToken(token);
    },
    /**
     * @description 设置用户名
     * @param {*} username
     */
    setUserId(userId) {
      this.userId = userId;
    },
    /**
     * @description 设置用户名
     * @param {*} username
     */
    setUsername(username) {
      this.username = username;
    },
    /**
     * @description 设置头像
     * @param {*} avatar
     */
    setAvatar(avatar) {
      this.avatar = avatar;
    },
    /**
     * @description 登录拦截放行时，设置虚拟角色
     */
    setVirtualRoles() {
      const aclStore = useAclStore();
      aclStore.setFull(true);
      this.setUsername('admin(未开启登录拦截)');
      this.setAvatar('https://i.gtimg.cn/club/item/face/img/2/15922_100.gif');
    },
    /**
     * @description 设置token并发送提醒
     * @param {string} token 更新令牌
     * @param {string} tokenName 令牌名称
     */
    afterLogin(res, showMessage) {
      const settingsStore = useSettingsStore();
      if (res.accessToken) {
        // console.log('afterLogin res', JSON.stringify(res))
        this.setToken(res.accessToken);
        this.setUserId(res.userId);
        this.setUsername(res.user.userName);
        if (!showMessage) {
          return;
        }
        const hour = new Date().getHours();
        const thisTime = hour < 8 ? '早上好' : hour <= 11 ? '上午好' : hour <= 13 ? '中午好' : hour < 18 ? '下午好' : '晚上好';
        gp.$baseNotify(`欢迎登录${settingsStore.title}`, `${thisTime}！`);
      } else {
        const err = `登录接口异常，未正确返回令牌...`;
        gp.$baseMessage(err, 'error', 'vab-hey-message-error');
        throw err;
      }
      // 加载权限
      // axios.get(`/api/identity/authorization/permissions`).then((res) => {
      //   const permissions = res
      //   console.log(permissions)
      // })
    },
    /**
     * @description 登录
     * @param {*} userInfo
     */
    async login(userInfo) {
      const res = await login(userInfo);
      return res;
    },
    /**
     * @description 第三方登录
     * @param {*} tokenData
     */
    async socialLogin(tokenData) {
      const {
        data: {
          [tokenName]: token
        }
      } = await socialLogin(tokenData);
      this.afterLogin(token, tokenName);
    },
    /**
     * @description 获取用户信息接口 这个接口非常非常重要，如果没有明确底层前逻辑禁止修改此方法，错误的修改可能造成整个框架无法正常使用
     * @returns
     */
    async getUserInfo() {
      const data = await axios.get(`/api/identity/authorization/permissions`);
      const aclStore = useAclStore();
      aclStore.setFull(false);
      // 每次刷新从服务端同步最新的用户信息，避免跨 Tab 切换租户后本地数据不一致
      const uid = JSON.parse(sessionStorage.getItem('user')).id;
      if (data.userData) {
        this.setUserId(data.userData.id);
        this.setUsername(data.userData.userName);
        if (data.userData.roleNames) aclStore.setRole(data.userData.roleNames);
        sessionStorage.setItem('user', JSON.stringify(data.userData));
      }
      // 同步最新的 belongId 和租户信息
      const stBelongId = sessionStorage.getItem('belongId');
      if (data.belongId) {
        sessionStorage.setItem('belongId', data.belongId);
        const tenant = await axios.get(`/api/multi-tenancy/tenants/${data.belongId}`);
        sessionStorage.setItem('tenant', JSON.stringify(tenant));
        if (stBelongId != data.belongId) {
          sessionStorage.removeItem('flowToken');
          const flowToken = await ensureToken(); // 保证有 token
          sessionStorage.setItem('flowToken', flowToken);
        }
      }
      // 有新的 user/belongId更新 CCFlow 组件的flowToken
      if (data.userData && data.belongId && (uid != this.userId || stBelongId != data.belongId)) {
        sessionStorage.removeItem('flowToken');
        const flowToken = await ensureToken(); // 保证有 token
        sessionStorage.setItem('flowToken', flowToken);
      }
      if (data.grantedPermissions) {
        aclStore.setPermission(data.grantedPermissions);
      }
      // 获取全局参数并存储
      let roundingRule = await getBaseSetting('RoundingRule');
      sessionStorage.setItem('roundingRule', roundingRule);
    },
    /**
     * @description 退出登录
     */
    async logout() {
      await logout();
      await this.resetAll();
      closeAllModals();
      // 解决横向布局退出登录显示不全的bug
      location.reload();
    },
    /**
     * @description 重置token、roles、permission、router、tabsBar等
     */
    async resetAll() {
      this.setToken('');
      this.setUsername('游客');
      this.setAvatar('https://i.gtimg.cn/club/item/face/img/2/15922_100.gif');
      const aclStore = useAclStore();
      const routesStore = useRoutesStore();
      const tabsStore = useTabsStore();
      aclStore.setPermission([]);
      aclStore.setFull(false);
      aclStore.setRole([]);
      tabsStore.delAllVisitedRoutes();
      routesStore.clearRoutes();
      await resetRouter();
      removeToken();
      sessionStorage.clear();
    }
  }
});